import { useCallback } from 'react'

import qs from '@scoir/query-string'
import window from 'browser-lib/window'
import { connect } from 'react-redux'
import { useLocation } from 'react-router'

import api from 'common/api'
import { useTrackEventsSessionId } from 'common/use-track-events-session-id'
import { useClearClickTrackingQueryParams, useClickTrackingQueryParams } from 'components/QueryParamsProvider'
import { actions as snackbarActions } from 'store/snackbar'

import StyledButton from '../styledButton'
import googleIcon from './google-icon.svg'

type LoginWithGoogleButtonProps = {
  addSnack: (message: string) => void
  scid?: number
  source?: string
  inviteToken?: string
  onError?: (message: string) => void
  // False where this button is the only way in, so a failure cannot send the
  // student looking for a sign-in method the page does not offer
  hasAlternatives?: boolean
}

const INVALID_INVITE_MESSAGE = 'This sign-in link is no longer valid. Please ask your school to send a new invitation.'
const RETRY_MESSAGE = "We couldn't start sign in with Google. Please try again."
const GENERIC_MESSAGE = 'Please select an alternate sign in method'

export const LoginWithGoogleButton = ({
  addSnack,
  source,
  scid,
  inviteToken,
  onError,
  hasAlternatives = true,
}: LoginWithGoogleButtonProps) => {
  const { pathname } = useLocation()
  const sessionId = useTrackEventsSessionId()
  const clickTrackingQueryParams = useClickTrackingQueryParams()
  const onClearClickTrackingQueryParams = useClearClickTrackingQueryParams()

  const reportError = useCallback(
    (message: string) => {
      if (onError) {
        onError(message)
        return
      }
      addSnack(message)
    },
    [addSnack, onError],
  )

  const onClick = useCallback(() => {
    const params = {
      ...clickTrackingQueryParams,
      scid: scid && scid > 0 ? scid.toString() : clickTrackingQueryParams.scid,
      source: source,
      action: pathname.includes(`/signin`) ? `login` : undefined,
      inviteToken,
      sessionId,
    }
    const finalParams = qs.stringify(params)
    try {
      if (inviteToken) {
        // Read back by the Google callback page so an error lands on the page the
        // student started from rather than the sign-in screen
        window.sessionStorage.setItem('oauthConfirmReturnUrl', window.location.href)
      } else {
        // The callback only clears this on the paths that consume it, so drop a
        // return url left behind by an abandoned invitation before it can
        // misroute this flow
        window.sessionStorage.removeItem('oauthConfirmReturnUrl')
      }
    } catch {
      // A browser with site data blocked still gets to Google, it just loses
      // the trip home
    }
    api
      .get<string>(`/api/google/init-oauth${finalParams ? `?${finalParams}` : ''}`)
      .then((url) => {
        url = decodeURIComponent(url.replace(/"/g, '')).replace(/\\u0026/g, '&')
        window.location.href = url
        onClearClickTrackingQueryParams()
      })
      .catch((response) => {
        // Only an invitation can be refused as invalid
        if (inviteToken && response?.status === 401) {
          reportError(INVALID_INVITE_MESSAGE)
          return
        }
        reportError(hasAlternatives && !inviteToken ? GENERIC_MESSAGE : RETRY_MESSAGE)
      })
  }, [
    reportError,
    pathname,
    sessionId,
    scid,
    source,
    inviteToken,
    clickTrackingQueryParams,
    onClearClickTrackingQueryParams,
    hasAlternatives,
  ])

  return (
    <StyledButton
      onClick={onClick}
      icon={googleIcon}
      ssoTitle="Google"
    />
  )
}

export default connect(null, {
  addSnack: snackbarActions.addSnackbarItem,
})(LoginWithGoogleButton)
